import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Chip, Typography, useTheme, InputBase, IconButton } from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import RouteIcon from "@mui/icons-material/Route";
import { API_BASE_URL } from "../../config";

const Drivers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/drivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, page, search, limit: 20 }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch drivers");
      }

      const data = await response.json();
      setDrivers(data.data || []);
      setRowCount(data.total || (data.data ? data.data.length : 0));
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const getCarLabel = (car) => {
    const cars = { 1: "Classic", 2: "Confort", 3: "VIP", 4: "Woman", 5: "Express" };
    return cars[car] || "Classic";
  };

  const getVerifiedChip = (status) => {
    switch (status) {
      case 1:
        return <Chip label="Validé" size="small" sx={{ backgroundColor: colors.greenAccent[600], color: "#fff" }} />;
      case 2:
        return <Chip label="Rejeté" size="small" sx={{ backgroundColor: "#d32f2f", color: "#fff" }} />;
      default:
        return <Chip label="En attente" size="small" sx={{ backgroundColor: "#f57c00", color: "#fff" }} />;
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.7 },
    {
      field: "name",
      headerName: "Nom Complet",
      flex: 1.1,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Box>
          <Typography fontWeight="600" color={colors.greenAccent[300]}>
            {params.row.name} {params.row.fname}
          </Typography>
          <Typography variant="caption" color={colors.grey[300]}>
            @{params.row.username}
          </Typography>
        </Box>
      ),
    },
    { field: "phone", headerName: "Téléphone", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.1 },
    {
      field: "car",
      headerName: "Véhicule",
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={getCarLabel(params.row.car)} variant="outlined" size="small" sx={{ borderColor: colors.grey[400], color: colors.grey[100] }} />
      ),
    },
    {
      field: "is_online",
      headerName: "Statut",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          label={params.value ? "En ligne" : "Hors ligne"}
          size="small"
          sx={{ backgroundColor: params.value ? colors.greenAccent[700] : colors.primary[500], color: "#fff" }}
        />
      ),
    },
    {
      field: "verified",
      headerName: "Dossier",
      flex: 0.9,
      renderCell: (params) => getVerifiedChip(params.value),
    },
    {
      field: "subscription",
      headerName: "Abonnement",
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          return <Typography variant="caption" color="#d32f2f">Expiré / Inactif</Typography>;
        }
        const exp = new Date(params.value);
        const isActive = exp > new Date();
        return (
          <Typography variant="caption" color={isActive ? colors.greenAccent[400] : "#d32f2f"} fontWeight="bold">
            {isActive ? `Actif jusqu'au ${exp.toLocaleDateString('fr-FR')}` : `Expiré (${exp.toLocaleDateString('fr-FR')})`}
          </Typography>
        );
      },
    },
    {
      field: "rides_link",
      headerName: "Courses",
      flex: 0.8,
      renderCell: ({ row: { id } }) => (
        <Button
          component={Link}
          to={`/driver/rides/${id}`}
          size="small"
          variant="contained"
          startIcon={<RouteIcon />}
          sx={{ backgroundColor: colors.blueAccent[600], fontSize: "11px" }}
        >
          Courses
        </Button>
      ),
    },
    {
      field: "edit",
      headerName: "Modifier",
      flex: 0.8,
      renderCell: ({ row: { id } }) => (
        <Button
          component={Link}
          to={`/edit/driver/drivers/${id}`}
          size="small"
          variant="contained"
          startIcon={<EditIcon />}
          sx={{ backgroundColor: colors.greenAccent[600], fontSize: "11px" }}
        >
          Éditer
        </Button>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  return (
    <Box m="20px">
      <Header title="CHAUFFEURS LINKERVTC" subtitle="Annuaire des chauffeurs, statuts d'activité et véhicules" />

      <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" mt="15px">
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 2, flex: 1 }}
          placeholder="Rechercher par nom, prénom, téléphone ou email..."
        />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={drivers}
          columns={columns}
          slots={{ toolbar: CustomToolbar }}
          pagination
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(newModel) => setPage(newModel.page + 1)}
        />
      </Box>
    </Box>
  );
};

export default Drivers;
